/**
 * A note's text: plain words with a little formatting, kept as a string.
 *
 * The stored form is a small markup, so a note's text stays readable in a
 * version diff and the server keeps treating it as text:
 *
 * - `**bold**`, `*italic*`, `++underline++`, `~~strikethrough~~`. Each marker
 *   toggles its mark, so marks nest and overlap in any order.
 * - A line starting `- ` is a bullet; `1. ` (any number) a numbered item.
 * - A backslash makes the next character literal: `\*`, `\\`, and a leading
 *   `\-` or `3\.` for a line that only looks like a list.
 *
 * Marks never cross a line: each is closed at the end of its line and opened
 * again on the next, so a line can be read on its own.
 */

export interface Marks {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
}

export type MarkName = keyof Marks;
export const MARK_NAMES: readonly MarkName[] = ["bold", "italic", "underline", "strike"];

export interface Run extends Marks {
  text: string;
}

export type BlockKind = "paragraph" | "bullet" | "number";

export interface Block {
  kind: BlockKind;
  runs: Run[];
}

const MARKERS: readonly { token: string; mark: MarkName }[] = [
  // Longest first: `**` is bold, `*` alone is italic.
  { token: "**", mark: "bold" },
  { token: "++", mark: "underline" },
  { token: "~~", mark: "strike" },
  { token: "*", mark: "italic" },
];

function sameMarks(a: Marks, b: Marks): boolean {
  return MARK_NAMES.every((mark) => Boolean(a[mark]) === Boolean(b[mark]));
}

function marksOf(run: Marks): Marks {
  const marks: Marks = {};
  for (const mark of MARK_NAMES) if (run[mark]) marks[mark] = true;
  return marks;
}

/** Adjacent runs with the same marks as one, and no empty runs. */
export function normaliseRuns(runs: readonly Run[]): Run[] {
  const out: Run[] = [];
  for (const run of runs) {
    if (!run.text) continue;
    const last = out[out.length - 1];
    if (last && sameMarks(last, run)) last.text += run.text;
    else out.push({ ...marksOf(run), text: run.text });
  }
  return out;
}

function parseInline(line: string): Run[] {
  const runs: Run[] = [];
  let marks: Marks = {};
  let text = "";
  const flush = () => {
    if (text) runs.push({ ...marks, text });
    text = "";
  };
  for (let i = 0; i < line.length; ) {
    if (line[i] === "\\" && i + 1 < line.length) {
      text += line[i + 1];
      i += 2;
      continue;
    }
    const marker = MARKERS.find(({ token }) => line.startsWith(token, i));
    if (marker) {
      flush();
      marks = { ...marks, [marker.mark]: !marks[marker.mark] };
      i += marker.token.length;
      continue;
    }
    text += line[i];
    i += 1;
  }
  flush();
  return normaliseRuns(runs.map((run) => ({ ...marksOf(run), text: run.text })));
}

export function parseNote(text: string): Block[] {
  return text.split(/\r?\n/).map((line) => {
    const bullet = /^- /.exec(line);
    if (bullet) return { kind: "bullet", runs: parseInline(line.slice(2)) };
    const number = /^\d+\. /.exec(line);
    if (number) return { kind: "number", runs: parseInline(line.slice(number[0].length)) };
    return { kind: "paragraph", runs: parseInline(line) };
  });
}

function escapeText(text: string): string {
  return (
    text
      .replace(/\\/g, "\\\\")
      .replace(/\*/g, "\\*")
      // Only a pair is a marker. A single + or ~ stays as it is, unless it
      // ends the run, where a marker may follow and make a pair of it.
      .replace(/\+(?=\+)/g, "\\+")
      .replace(/~(?=~)/g, "\\~")
      .replace(/\+$/, "\\+")
      .replace(/~$/, "\\~")
  );
}

function serializeInline(runs: readonly Run[]): string {
  let out = "";
  let open: Marks = {};
  const toggle = (next: Marks) => {
    for (const { token, mark } of MARKERS) {
      if (Boolean(open[mark]) !== Boolean(next[mark])) out += token;
    }
    open = marksOf(next);
  };
  for (const run of normaliseRuns(runs)) {
    toggle(run);
    out += escapeText(run.text);
  }
  toggle({});
  return out;
}

export function serializeNote(blocks: readonly Block[]): string {
  let number = 0;
  return blocks
    .map((block) => {
      const body = serializeInline(block.runs);
      if (block.kind === "bullet") {
        number = 0;
        return `- ${body}`;
      }
      if (block.kind === "number") {
        number += 1;
        return `${number}. ${body}`;
      }
      number = 0;
      // A paragraph that merely looks like a list item keeps looking like text.
      return body.replace(/^- /, "\\- ").replace(/^(\d+)\. /, "$1\\. ");
    })
    .join("\n");
}

/** The words alone, for measuring and for anything that wants plain text. */
export function plainText(text: string): string {
  return parseNote(text)
    .map((block) => block.runs.map((run) => run.text).join(""))
    .join("\n");
}

/** The number each numbered item shows: counted within its run of numbered items. */
export function itemNumbers(blocks: readonly Block[]): (number | null)[] {
  let count = 0;
  return blocks.map((block) => {
    if (block.kind !== "number") {
      count = 0;
      return null;
    }
    count += 1;
    return count;
  });
}

// --- The editor's HTML -------------------------------------------------------

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const TAGS: Record<MarkName, string> = { bold: "b", italic: "i", underline: "u", strike: "s" };

function runsToHtml(runs: readonly Run[]): string {
  if (runs.length === 0) return "<br>";
  return runs
    .map((run) => {
      let html = escapeHtml(run.text);
      for (const mark of MARK_NAMES) if (run[mark]) html = `<${TAGS[mark]}>${html}</${TAGS[mark]}>`;
      return html;
    })
    .join("");
}

/** HTML for a contentEditable editor: paragraphs as divs, lists as lists. */
export function noteToHtml(text: string): string {
  const blocks = parseNote(text);
  let html = "";
  let list: "ul" | "ol" | null = null;
  for (const block of blocks) {
    const wanted = block.kind === "bullet" ? "ul" : block.kind === "number" ? "ol" : null;
    if (list && list !== wanted) {
      html += `</${list}>`;
      list = null;
    }
    if (wanted && !list) {
      html += `<${wanted}>`;
      list = wanted;
    }
    html += wanted ? `<li>${runsToHtml(block.runs)}</li>` : `<div>${runsToHtml(block.runs)}</div>`;
  }
  if (list) html += `</${list}>`;
  return html;
}

const BLOCK_TAGS = new Set(["DIV", "P", "LI", "UL", "OL", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE"]);

function marksFromElement(element: Element, inherited: Marks): Marks {
  const marks: Marks = { ...inherited };
  const tag = element.tagName;
  if (tag === "B" || tag === "STRONG") marks.bold = true;
  if (tag === "I" || tag === "EM") marks.italic = true;
  if (tag === "U" || tag === "INS") marks.underline = true;
  if (tag === "S" || tag === "STRIKE" || tag === "DEL") marks.strike = true;
  const style = (element as HTMLElement).style;
  if (style) {
    const weight = style.fontWeight;
    if (weight === "bold" || weight === "bolder" || Number(weight) >= 600) marks.bold = true;
    if (weight === "normal" || (weight !== "" && Number(weight) > 0 && Number(weight) < 600)) delete marks.bold;
    if (style.fontStyle === "italic") marks.italic = true;
    if (style.fontStyle === "normal") delete marks.italic;
    const decoration = `${style.textDecoration} ${style.textDecorationLine}`;
    if (decoration.includes("underline")) marks.underline = true;
    if (decoration.includes("line-through")) marks.strike = true;
  }
  return marks;
}

/**
 * Read an editor's content back into note text. Only text and the four marks
 * survive: anything else a browser put there (spans, fonts, colours) is
 * dropped, so what is stored is always the markup above.
 *
 * Browsers lay lines out differently (a bare first line then divs, or lines
 * split by <br>), so a line is whatever lies between block boundaries and
 * line breaks.
 */
export function noteFromElement(root: Element): string {
  const blocks: Block[] = [];
  let current: Block | null = null;
  const open = (kind: BlockKind): Block => {
    const opened: Block = { kind, runs: [] };
    blocks.push(opened);
    current = opened;
    return opened;
  };

  const walk = (node: Node, marks: Marks, kind: BlockKind) => {
    if (node.nodeType === 3) {
      const text = (node.textContent ?? "").replace(/\u00a0/g, " ").replace(/[\r\n]+/g, " ");
      if (text) (current ?? open(kind)).runs.push({ ...marks, text });
      return;
    }
    if (node.nodeType !== 1) return;
    const element = node as Element;
    const tag = element.tagName;
    if (tag === "BR") {
      // Ends the line. With no line open, it is an empty line of its own.
      if (current === null) open(kind);
      current = null;
      return;
    }
    if (tag === "UL" || tag === "OL") {
      current = null;
      for (const child of element.childNodes) walk(child, marks, tag === "UL" ? "bullet" : "number");
      current = null;
      return;
    }
    const childMarks = marksFromElement(element, marks);
    if (BLOCK_TAGS.has(tag)) {
      const blockKind: BlockKind = tag === "LI" && kind !== "paragraph" ? kind : tag === "LI" ? "bullet" : kind;
      current = null;
      open(blockKind);
      for (const child of element.childNodes) walk(child, childMarks, blockKind);
      current = null;
      return;
    }
    for (const child of element.childNodes) walk(child, childMarks, kind);
  };

  for (const child of root.childNodes) walk(child, {}, "paragraph");
  const cleaned = blocks.map((entry) => ({ ...entry, runs: normaliseRuns(entry.runs) }));
  return serializeNote(cleaned).replace(/\s+$/, "");
}
